import BankIcon from 'assets/svg/icons/bankIcon';
import { Modal, type ModalApi } from 'components/atoms/Modal';
import UseEffect from 'components/atoms/UseEffect';
import CustomLoader from 'components/molecules/CustomLoader.component';
import ModalHeading from 'components/molecules/ModalHeading';
import SuccessFeedbackWithConfettiBurst from 'components/molecules/SuccessFeedbackWithConfettiBurst.component';
import UnifiedBankingSignupFlow from 'components/pages/Banks/BankingSignUp/components/UnifiedBankingSignupFlow';
import { WalletOnboarding } from 'components/pages/Banks/BankingSignUp/components/WalletOnboarding.component';
import MoneyTransfer from 'components/pages/Banks/Wallet/MoneyTransfer';
import type { MoneyTransferInitialData } from 'components/pages/Banks/Wallet/MoneyTransfer/types';
import { useGetActivationStatus } from 'hooks/Bank/useActivationStatus';
import useCanAccessAccountableBanking from 'hooks/Bank/useCanAccessAccountableBanking';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import useHasAccountableBanking from 'hooks/Bank/useHasAccountableBanking';
import useInvalidateTransferDependencies from 'hooks/Bank/useInvalidateTransferDependencies';
import { useIsInActivationFlowExperiment } from 'hooks/Bank/useIsInActivationFlowExperiment';
import useQueryParams from 'hooks/shared/useQueryParams';
import { worldThemeContext } from 'hooks/shared/useWorldTheme';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import useModal from 'hooks/useModal';
import { MoneyTransferContext, type TransferConfig } from 'hooks/useTransfer';
import { motion } from 'motion/react';
import { useMemo, useRef, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getTracingEvents } from 'utils/amplitude';
import { WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition, isWeb } from 'utils/constants';

const tracingEvents = getTracingEvents({
  onTransferSuccess: WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER,
});

const MoneyTransferProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  const qs = useQueryParams();

  const successfulPaymentModalRef = useRef<ModalApi>();

  const [modalContent, modalApi] = useModal({
    width: '2xl',
    name: 'Accountable Banking payment transfer modal',
    color: 'bank',
  });

  const navigate = useCustomNavigate();

  const invalidateTransferDeps = useInvalidateTransferDependencies();

  const { isLoading } = useGetConnectors();

  const canAccessAccountableBanking = useCanAccessAccountableBanking();

  const hasAccountableBanking = useHasAccountableBanking();

  const { data: activationStatus } = useGetActivationStatus();

  const isUnifiedActivationFlowEnabled = useIsInActivationFlowExperiment();

  // Check if we should show activation flow (first transfer check)
  const shouldShowActivation = useMemo(() => {
    // Only show if user has never started activation (null means first transfer)
    return activationStatus?.activationFurthestStepReached === null;
  }, [activationStatus]);

  const transfer = (
    initialData: MoneyTransferInitialData = {},
    config?: TransferConfig,
  ) => {
    const { theme = 'bank', isStandingOrderPayment = false } = config || {};

    modalApi.open(
      () => (
        <worldThemeContext.Provider value={theme || 'bank'}>
          {isLoading ? (
            <div className="min-h-[300px] flex items-center justify-center">
              <CustomLoader />
            </div>
          ) : !isWeb ? (
            <ModalHeading
              title={t('accountable_banking.forbid_expert_transfer.title')}
              subtitle={t(
                'accountable_banking.forbid_expert_transfer.subtitle',
              )}
              Icon={BankIcon}
            />
          ) : !canAccessAccountableBanking ? (
            <ModalHeading
              title={t('accountable_banking')}
              subtitle={t('accountable_banking.no_access')}
              Icon={BankIcon}
            />
          ) : !hasAccountableBanking ? (
            isUnifiedActivationFlowEnabled ? (
              <UnifiedBankingSignupFlow
                onComplete={() => {
                  // Pre-account flow completed - user will be redirected to Swan
                  // No need to do anything here as redirect is handled in ConfirmIdentity step
                }}
              />
            ) : (
              <WalletOnboarding
                onClose={() => {
                  modalApi.close();
                }}
              />
            )
          ) : (
            <MoneyTransfer
              initialData={initialData}
              onTransferSuccess={() => {
                modalApi.close();
                config?.onSuccess?.();
                setTimeout(
                  () => successfulPaymentModalRef.current?.openModal(),
                  1000,
                );
              }}
              isStandingOrderPayment={isStandingOrderPayment}
            />
          )}
        </worldThemeContext.Provider>
      ),
      { color: theme, name: 'Wallet send money modal' },
    );
  };

  return (
    <MoneyTransferContext.Provider value={{ transfer }}>
      {modalContent}

      <Modal
        name="Successful payment"
        apiRef={successfulPaymentModalRef}
        className="!max-w-[44rem]"
        color="bank"
        onClose={
          qs.successfulPayment === 'true'
            ? () => {
                navigate('/banks');

                invalidateTransferDeps();
              }
            : () => {}
        }
        defaultOpen={
          qs.successfulPayment === 'true' ||
          qs.successfulBasketPayment === 'true'
        }
        render={() => (
          <UseEffect
            effect={() => tracingEvents.onTransferSuccess()}
            deps={[]}
            render={() =>
              shouldShowActivation && isUnifiedActivationFlowEnabled ? (
                <UnifiedBankingSignupFlow
                  onComplete={() => {
                    successfulPaymentModalRef.current?.closeModal();
                    invalidateTransferDeps();
                    navigate('/banks');
                  }}
                />
              ) : shouldShowActivation && !isUnifiedActivationFlowEnabled ? (
                <WalletOnboarding
                  onClose={() => {
                    successfulPaymentModalRef.current?.closeModal();
                    invalidateTransferDeps();
                    navigate('/banks');
                  }}
                />
              ) : (
                <motion.div
                  {...fadeTransition}
                  key="3"
                  className="flex items-center flex-col relative min-h-[400px] w-full"
                >
                  <SuccessFeedbackWithConfettiBurst
                    message={
                      qs.successfulBasketPayment === 'true'
                        ? t(
                            'accountable_banking.expenses_basket.payment.success_msg',
                          )
                        : qs.isScheduledPayment === 'true'
                        ? t('accountable_banking.scheduled_payment.success_msg')
                        : t('accountable_banking.successful_payment_msg')
                    }
                  />
                </motion.div>
              )
            }
          />
        )}
      />

      {children}
    </MoneyTransferContext.Provider>
  );
};

export default MoneyTransferProvider;
